import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { AgGridReact } from 'ag-grid-react';
import ReactResizeDetector from 'react-resize-detector';
import { isArray, map, values, compact, cloneDeep, reduce, keyBy } from 'lodash';
import { Tooltip, OverlayTrigger, Button } from 'react-bootstrap';
import { withRouter, Link } from 'react-router-dom';
import Fa from 'react-fontawesome';
import NumberDisplay from '../../../../components/number-display';
import ProgressBar from '../../../../components/progress-bar';
import SearchField from '../../../../components/search-field';
import { requestUpdateCustomFields, loadMetaData } from '../../SetupActions';
// Import Selectors
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import { getViewableDimensions } from '../../../App/AppReducer';
import CRMInstanceName from '../../../../components/crm-instance-name';

function CodeCellRenderer(props) {
  return (
    <code>{props.value}</code>
  );
}
function NameCellRenderer(props) {
  const { data, value } = props;
  const hasDescription = data && data.description && data.description.length > 0;
  const display = (<span>{value}</span>);
  if (hasDescription) {
    const tooltip = (
      <Tooltip id={`${data.name}_tooltip`}>{data.description}</Tooltip>
    );
    return (
      <OverlayTrigger placement="right" overlay={tooltip}>
        <span>{display} <Fa name="info-circle" className="text-active" /></span>
      </OverlayTrigger>
    );
  }
  return display;
}

function ToggleCellRenderer(props) {
  const { data, checked, disabled } = props;
  const value = checked[data._id] === true;
  const alwaysSync = data.is_system === true;
  
  const style = { cursor: disabled ? 'not-allowed' : 'pointer' };
  let cls = value ? 'text-success' : '';
  let name = value ? 'check-square' : 'square-o';
  let spin = false;
  if (disabled) {
    cls = 'text-muted';
    if (value) {
      name = 'spinner';
      spin = true;
    }
  }
  
  if (alwaysSync) {
    style.cursor = 'not-allowed';
    const tooltip = (
      <Tooltip id={`${data.name}_always_tooltip`}>Required TruSales field</Tooltip>
    );
    return (
      <div className="text-center" style={style}>
        <OverlayTrigger placement="top" overlay={tooltip}>
          <Fa
            className="text-active"
            style={{ fontSize: '1.3em' }}
            name="check-square"
          />
        </OverlayTrigger>
      </div>
    );
  }
  return (
    <div className="text-center" style={style}>
      <Fa
        className={cls}
        style={{ fontSize: '1.3em' }}
        name={name}
        spin={spin}
      />
    </div>
  );
}

class CustomFieldDetailPage extends Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      loaded: false,
      fields: [],
      checked: {},
      dirty: false,
      showAddModal: false,
      navHeight: 0,
      objectName: null,
      objectApiName: null,
      errorMessage: null,
    };
    this.handleSearch = this.handleSearch.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.onResizeContainer = this.onResizeContainer.bind(this);
    this.checkValueGetter = this.checkValueGetter.bind(this);
    this.handleCellToggle = this.handleCellToggle.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleDownload = this.handleDownload.bind(this);
    this.getContextMenuItems = this.getContextMenuItems.bind(this);
  }

  componentDidMount() {
    if (this.props.match.params.objectName) {
      this.loadCustomFields(this.props.match.params.objectName);
    }
  }


  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  onResizeContainer(width, navHeight) {
    this.setState({ navHeight });
  }

  getContextMenuItems(/* params */) {
    const results = [
      'copy',
      'copyWithHeaders',
    ];
    return results;
  }

  handleDownload() {
    if (this.gridApi) {
      const { objectName } = this.state;
      const params = {
        columnGroups: true,
        fileName: `${objectName} Custom Fields - TruSales`,
        sheetName: `${objectName} Custom Fields`,
        exportMode: 'xlsx',
      };
      this.gridApi.exportDataAsExcel(params);
    }
  }

  handleCellToggle(params) {
    const { checked, fields, disabled } = this.state;
    const { data } = params;
    // const { name } = data;
    if (disabled || data.is_system) {
      return;
    }
    // const sortState = this.gridApi ? this.gridApi.getSortModel() : null;
    const filterState = this.gridApi ? this.gridApi.getFilterModel() : null;
    const columnState = this.gridColumnApi ? this.gridColumnApi.getColumnState() : null;
    const groupState = this.gridColumnApi ? this.gridColumnApi.getColumnGroupState() : null;
   
    const syncRequired = checked[data._id] === true;
    const newChecked = { ...checked, [data._id]: !syncRequired };

    let dirty = false;
    map(fields, (f) => {
      if (newChecked[f._id] !== f.sync_required) {
        dirty = true;
      }
    });
    this.setState({ checked: newChecked, dirty, errorMessage: null }, () => {
      if (this.gridApi) {
        // this.gridApi.setSortModel(sortState);
        this.gridApi.setFilterModel(filterState);
      }
      if (this.gridColumnApi) {
        this.gridColumnApi.applyColumnState(columnState);
        this.gridColumnApi.setColumnGroupState(groupState);
      }
    });
  }

  handleSubmit() {
    const { checked } = this.state;
    this.setState({ disabled: true, errorMessage: null }, () => {
      this.props.dispatch(requestUpdateCustomFields(this.props.match.params.objectName, checked))
        .then(
          (results) => {
            this.initializeData(results, () => {
              this.setState({ disabled: false, errorMessage: null });
            });
          },
          (e) => {
            let msg = null;
            if (e && e.data && isArray(e.data)) {
              msg = e.data.join('. ');
            } else if (e.data && e.data.message) {
              msg = e.data.message;
            } else {
              msg = 'Unknown error';
            }
            this.setState({ disabled: false, errorMessage: msg });
          },
        );
    });

  }

  handleSearch(query) {
    if (this.gridApi) {
      this.gridApi.setQuickFilter(query);
    }
  }

  handleReset() {
    const { fields } = this.state;
    const checked = {};
    map(fields, (field) => {
      checked[field._id] = field.sync_required === true;
    });
    this.setState({ checked, dirty: false });
  }

  
  initializeData(results, cb = () => {}) {
    const checked = {};
    const fields = results.map((field) => {
      const f = cloneDeep(field);
      checked[f._id] = f.sync_required === true;
      return f;
    });
    this.setState({ fields, checked }, cb);
  }

  loadCustomFields(objectName) {
    if (!objectName) {
      return;
    }
    this.setState({
      loading: true, loaded: false, fields: [], checked: {}, dirty: false,
    });
    const allFieldsPromise = this.props.dispatch(loadMetaData({ name: objectName, syncRequired: true, withFields: true }));
    allFieldsPromise.then(
      (objects) => {
        if (objects && objects.length > 0) {
          const results = objects[0].fields || [];
          const name = objects[0].label;
          const objectApiName = objects[0].name;
          this.initializeData(results, () => {
            this.setState({
              objectName: name, objectApiName, loading: false, loaded: true, dirty: false,
            });
          });
        } else {
          this.setState({
            loading: false, loaded: false, fields: [], checked: {}, dirty: false,
          });
        }
      },
      () => {
        this.setState({
          loading: false, loaded: false, fields: [], checked: {}, dirty: false,
        });
      },
    );
    return allFieldsPromise;
  }

  checkValueGetter(props) {
    const { data } = props;
    if (!data) {
      return null;
    }
    const { checked } = this.state;
    return checked[data._id] === true || data.is_system;
  }

  renderInstruction() {
    const { objectApiName } = this.state;
    return (
      <div className="well well-sm">
        <div>
          <p>
            The table below displays the custom fields defined on your organization&rsquo;s&nbsp;
            <strong><code>{objectApiName}</code> <CRMInstanceName /> object</strong> that can be enabled on TruSales.
            Disabling a field means that field will not be available for use in TruSales.
          </p>
        </div>
      </div>
    );
  }

  renderInvalidPage() {
    const { match: { params: { objectName } } } = this.props;
    return (
      <div className="alert alert-danger">
        Invalid Object Name: <code>{objectName}</code>
      </div>
    );
  }

  
  renderEmpty() {
    const { objectApiName } = this.state;
    return (
      <div className="alert alert-warning">No fields found for <code>{objectApiName}</code> object.</div>
    )
  }

  renderSubmitButton() {
    const { dirty, disabled, checked } = this.state;
    if (!dirty) {
      return null;
    }
    const total = compact(values(checked));
    return (
      <div className="navbar-form navbar-right">
        <Button onClick={this.handleReset} disabled={disabled} type="button" bsStyle="link" className="animated flash">
          Cancel
        </Button>
        <Button onClick={this.handleSubmit} disabled={disabled} type="button" bsStyle="primary" className="animated flash">
          {!disabled && <span>Update <NumberDisplay value={total.length} suffix="Custom field" suffixIsUnit precision={0} /></span>}
          {disabled && <span><Fa name="spinner" spin /> Updating</span>}
        </Button>&nbsp;&nbsp;
      </div>
    );
  }

  renderDownloadButton() {
    if (!this.gridApi) {
      return null;
    }
    return (
      <div className="navbar-form navbar-right" style={{ paddingRight: 30 }}>
        <OverlayTrigger placement="top" overlay={<Tooltip id="custom-field-download-tip">Export as Excel File</Tooltip>}>
          <Button bsStyle="link" onClick={this.handleDownload}>
            <Fa name="download" />
          </Button>
        </OverlayTrigger>
      </div>
    );
  }

  renderTable() {
    const { fields, navHeight } = this.state;
    const { viewableDimensions: { height }, organization } = this.props;
    const isManual = organization.manual_sync === true;

    const columnDefs = [
      {
        headerName: 'Field Name',
        field: 'label',
        colId: 'label',
        width: 400,
        sortable: true,
        cellRenderer: NameCellRenderer,
        pinned: 'left',
      },
      {
        headerName: 'Api Name',
        field: 'name',
        colId: 'name',
        width: 400,
        sortable: true,
        cellRenderer: CodeCellRenderer,
      },
      {
        headerName: 'Data Type',
        field: 'type',
        colId: 'type',
        width: 150,
        sortable: true,
        cellRenderer: CodeCellRenderer,
      },
      ...(!isManual ? [
        {
          headerName: 'Use in TruSales?',
          width: 185,
          colId: 'use',
          sortable: true,
          cellRenderer: ToggleCellRenderer,
          valueGetter: this.checkValueGetter,
          onCellClicked: this.handleCellToggle,
          cellRendererParams: {
            disabled: this.state.disabled,
            checked: this.state.checked,
          },
        },
      ] : []),
    ];
    const defaultColDef = {
      suppressMenu: true,
      suppressMovable: true,
    };

    const h = Math.max(height - navHeight, 100);

    return (
      <div>
        <nav className="navbar navbar-default navbar-controls">
          <form className="navbar-form navbar-left">
            <SearchField onSearch={this.handleSearch} /> {this.state.dirty}
          </form>
          {this.renderDownloadButton()}
          {this.renderSubmitButton()}
          <ReactResizeDetector handleHeight onResize={this.onResizeContainer} />
        </nav>
        <div
          className="ag-theme-alpine"
          style={{ height: h, width: '100%' }} 
        >
          <AgGridReact
            reactNext
            columnDefs={columnDefs}
            rowData={fields}
            onGridReady={this.onGridReady}
            defaultColDef={defaultColDef}
            getContextMenuItems={this.getContextMenuItems}
          />
        </div>
      </div>
    )
  }

  renderErrorMessage() {
    const { errorMessage } = this.state;
    if (errorMessage !== null && errorMessage !== undefined && errorMessage.length > 0) {
      return (
        <div className="alert alert-danger">
          <p>{errorMessage}</p>
        </div>
      );
    }
    return null;
  }

  renderContent() {
    const { fields, objectName } = this.state;
    if (fields.length === 0) {
      return this.renderEmpty();
    }
    return (
      <div>
        <h2 className="page-sub-header">
          <Link to="/setup/sync-settings">Sync Settings</Link> <Fa className="text-muted" name="chevron-right" /> {objectName}
        </h2>
        {this.renderInstruction()}
        {this.renderErrorMessage()}
        {this.renderTable()}
      </div>
    );
  }

  render() {
    const { loading, loaded, objectName } = this.state;
   
    const isLoading = loading || !loaded;
    
    const title = objectName ? `${objectName} Fields | Sync Settings` : 'Sync Settings';

    return (
      <div>
        <Helmet title={title} />
        {!isLoading && this.renderContent()}
        {isLoading && <ProgressBar />}
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    organization: getUserCurrentOrganization(state),
    viewableDimensions: getViewableDimensions(state),
  };
}

CustomFieldDetailPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.shape({}).isRequired,
  match: PropTypes.shape({
    params: PropTypes.object,
  }).isRequired,
  organization: PropTypes.shape({}).isRequired,
  viewableDimensions: PropTypes.shape({}).isRequired,
};

CustomFieldDetailPage.defaultProps = {
};


CustomFieldDetailPage.contextTypes = {
  router: PropTypes.shape({}),
};

export default withRouter(connect(mapStateToProps)(CustomFieldDetailPage));
